/**
 * Staff Reports API — Phase 10A.6.
 *
 * Unified form-fill reports: instructor, office-manager, business-dev-executive.
 * Draft lifecycle, submit→PDF, refill, stats.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@core/api/client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ReportType = 'instructor' | 'office-manager' | 'business-dev-executive';
export type PeriodType = 'week' | 'biweek' | 'month' | 'bimonth' | 'quarter';
export type ReportStatus = 'draft' | 'submitted';

export interface StaffReport {
  id: string;
  report_type: ReportType;
  tenant_id: string;
  school_id?: string;
  author_uid: string;
  author_email: string;
  author_name: string;
  author_role: string;
  period_type: PeriodType;
  period_start: string;
  period_end: string;
  student_id?: string;
  student_name?: string;
  instructor_name?: string;
  discipline?: string;
  level?: string;
  form_data: Record<string, unknown>;
  status: ReportStatus;
  created_at: string;
  updated_at: string;
  submitted_at?: string;
  expires_at: string;
  pdf_storage_path?: string;
  pdf_ready: boolean;
  is_test: boolean;
  created_by_app: string;
}

export interface StaffReportListResponse {
  items: StaffReport[];
  total: number;
  has_more: boolean;
}

export interface PdfUrlResponse {
  download_url: string;
  expires_at: string;
}

export interface StaffReportCreate {
  report_type: ReportType;
  period_type: PeriodType;
  period_start: string;
  period_end: string;
  form_data?: Record<string, unknown>;
  student_id?: string;
  student_name?: string;
  instructor_name?: string;
  discipline?: string;
  level?: string;
  school_id?: string;
  status?: ReportStatus;
  created_by_app?: 'candeza' | 'rhythmnbeats' | 'api';
}

export interface StaffReportUpdate {
  form_data?: Record<string, unknown>;
  period_type?: PeriodType;
  period_start?: string;
  period_end?: string;
  student_id?: string;
  student_name?: string;
  instructor_name?: string;
  discipline?: string;
  level?: string;
}

export interface MyStats {
  total_submitted: number;
  by_type: Record<string, number>;
  this_week: number;
  this_month: number;
  this_quarter: number;
  last_report?: string;
}

export interface AdminDashboard {
  total_submitted: number;
  active_contributors: number;
  by_type: Record<string, number>;
  top_contributors: Array<{
    uid: string;
    name: string;
    email: string;
    total: number;
    by_type: Record<string, number>;
    last_submitted_at?: string;
  }>;
  monthly_trend: Array<{ month: string; count: number }>;
  recent_submissions: Array<{
    log_id: string;
    report_id: string;
    report_type: string;
    author_name: string;
    author_uid: string;
    period_start: string;
    period_end: string;
    created_at?: string;
  }>;
  archived_by_contributor: Array<{
    author_uid: string;
    author_name: string;
    reports: Array<{
      id: string;
      report_type: string;
      period_start: string;
      period_end: string;
      submitted_at?: string;
    }>;
  }>;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const staffReportKeys = {
  all: ['staff-reports'] as const,
  lists: () => [...staffReportKeys.all, 'list'] as const,
  list: (type: ReportType, filters: Record<string, unknown>) =>
    [...staffReportKeys.lists(), type, filters] as const,
  detail: (type: ReportType, id: string) =>
    [...staffReportKeys.all, 'detail', type, id] as const,
  myStats: () => ['report-stats', 'my'] as const,
  adminDashboard: () => ['report-stats', 'admin-dashboard'] as const,
};

// ---------------------------------------------------------------------------
// Hooks — List
// ---------------------------------------------------------------------------

export function useStaffReports(
  reportType: ReportType,
  filters: {
    status?: string;
    author_uid?: string;
    period_start?: string;
    period_end?: string;
    /** 11E.8 — Grades tab / viewer portal filter by student. */
    student_id?: string;
    limit?: number;
  } = {},
): UseQueryResult<StaffReportListResponse> {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.author_uid) params.set('author_uid', filters.author_uid);
  if (filters.period_start) params.set('period_start', filters.period_start);
  if (filters.period_end) params.set('period_end', filters.period_end);
  if (filters.student_id) params.set('student_id', filters.student_id);
  if (filters.limit) params.set('limit', String(filters.limit));

  const query = params.toString() ? `?${params}` : '';

  return useQuery({
    queryKey: staffReportKeys.list(reportType, filters),
    queryFn: () => api.get<StaffReportListResponse>(`/v1/reports/staff/${reportType}${query}`),
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Hooks — Get one
// ---------------------------------------------------------------------------

export function useStaffReport(
  reportType: ReportType,
  reportId: string,
  enabled = true,
): UseQueryResult<StaffReport> {
  return useQuery({
    queryKey: staffReportKeys.detail(reportType, reportId),
    queryFn: () => api.get<StaffReport>(`/v1/reports/staff/${reportType}/${reportId}`),
    enabled: enabled && !!reportId,
  });
}

// ---------------------------------------------------------------------------
// Convenience hooks
// ---------------------------------------------------------------------------

export function useMyDrafts(reportType: ReportType): UseQueryResult<StaffReportListResponse> {
  return useStaffReports(reportType, { status: 'draft', author_uid: 'me' });
}

export function useMyReports(reportType: ReportType): UseQueryResult<StaffReportListResponse> {
  return useStaffReports(reportType, { status: 'submitted', author_uid: 'me' });
}

// ---------------------------------------------------------------------------
// Mutations — Create
// ---------------------------------------------------------------------------

export function useCreateStaffReport(reportType: ReportType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: StaffReportCreate) =>
      api.post<StaffReport>(`/v1/reports/staff/${reportType}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: staffReportKeys.lists() });
    },
  });
}

// ---------------------------------------------------------------------------
// Mutations — Update
// ---------------------------------------------------------------------------

export function useUpdateStaffReport(reportType: ReportType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: StaffReportUpdate }) =>
      api.patch<StaffReport>(`/v1/reports/staff/${reportType}/${id}`, data),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: staffReportKeys.lists() });
      qc.setQueryData(staffReportKeys.detail(reportType, result.id), result);
    },
  });
}

// ---------------------------------------------------------------------------
// Mutations — Delete
// ---------------------------------------------------------------------------

export function useDeleteStaffReport(reportType: ReportType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<void>(`/v1/reports/staff/${reportType}/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: staffReportKeys.lists() });
    },
  });
}

// ---------------------------------------------------------------------------
// Mutations — Submit
// ---------------------------------------------------------------------------

export function useSubmitStaffReport(reportType: ReportType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<StaffReport>(`/v1/reports/staff/${reportType}/${id}/submit`),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: staffReportKeys.lists() });
      qc.setQueryData(staffReportKeys.detail(reportType, result.id), result);
      qc.invalidateQueries({ queryKey: staffReportKeys.myStats() });
    },
  });
}

// ---------------------------------------------------------------------------
// Mutations — Refill
// ---------------------------------------------------------------------------

export function useRefillStaffReport(reportType: ReportType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<StaffReport>(`/v1/reports/staff/${reportType}/${id}/refill`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: staffReportKeys.lists() });
    },
  });
}

// ---------------------------------------------------------------------------
// PDF URL
// ---------------------------------------------------------------------------

export function usePdfUrl(
  reportType: ReportType,
  reportId: string,
  enabled = false,
): UseQueryResult<PdfUrlResponse> {
  return useQuery({
    queryKey: ['staff-report-pdf', reportType, reportId],
    queryFn: () => api.get<PdfUrlResponse>(`/v1/reports/staff/${reportType}/${reportId}/pdf`),
    enabled: enabled && !!reportId,
    staleTime: 50 * 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Stats
// ---------------------------------------------------------------------------

export function useMyStats(): UseQueryResult<MyStats> {
  return useQuery({
    queryKey: staffReportKeys.myStats(),
    queryFn: () => api.get<MyStats>('/v1/reports/stats/my'),
    staleTime: 60_000,
  });
}

export function useAdminDashboard(): UseQueryResult<AdminDashboard> {
  return useQuery({
    queryKey: staffReportKeys.adminDashboard(),
    queryFn: () => api.get<AdminDashboard>('/v1/reports/dashboard'),
    staleTime: 60_000,
  });
}
